import { Badge, Button, Card, Group, SimpleGrid, Stack, Text } from '@mantine/core';
import { IconPlus, IconRobot } from '@tabler/icons-react';
import type { AdminCaseImage, AdminFinding, AiReviewSummary } from '../api/cases';
import { FindingAiPanel } from './finding-ai-panel';
import { ImageThumb } from './image-thumb';

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString();
}

function imageStatusColor(status: string): string {
  switch (status) {
    case 'processed':
      return 'green';
    case 'pending':
      return 'yellow';
    case 'failed':
    case 'quarantined':
      return 'red';
    default:
      return 'gray';
  }
}

interface Props {
  finding: AdminFinding;
  thumbnails: AdminCaseImage[]; // processed images w/ presigned URLs, already filtered to this finding
  reviews: AiReviewSummary[]; // already filtered to this finding, newest first
  optimisticQueued: boolean;
  canTriage: boolean;
  onRunReview: (findingId: string) => void;
  onAddImage: (findingId: string) => void;
  onZoomImage: (image: AdminCaseImage) => void;
  onRemoveImage: (image: AdminCaseImage) => void;
  onRetryImage: (findingId: string, imageId: string) => void;
}

export function FindingCard({
  finding,
  thumbnails,
  reviews,
  optimisticQueued,
  canTriage,
  onRunReview,
  onAddImage,
  onZoomImage,
  onRemoveImage,
  onRetryImage,
}: Props) {
  const hasProcessed = finding.images.some((i) => i.ingestion_status === 'processed');
  const pending = finding.images.filter((i) => i.ingestion_status !== 'processed');

  return (
    <Card withBorder padding="md" radius="sm" data-testid={`finding-card-${finding.id}`}>
      <Stack gap="xs">
        <Group gap="xs" align="center">
          <Badge variant="light" color="blue">
            {finding.finding_type}
          </Badge>
          {(finding.body_site ?? finding.body_site_free_text) && (
            <Text size="sm">{finding.body_site ?? finding.body_site_free_text}</Text>
          )}
          <Text size="xs" c="dimmed">
            {formatDate(finding.created_at)}
          </Text>
          {canTriage && (
            <>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconRobot size={14} />}
                disabled={!hasProcessed}
                onClick={() => onRunReview(finding.id)}
              >
                Run AI review
              </Button>
              <Button
                size="xs"
                variant="light"
                leftSection={<IconPlus size={14} />}
                onClick={() => onAddImage(finding.id)}
              >
                Add image
              </Button>
            </>
          )}
        </Group>

        {finding.clinical_notes && (
          <Text size="sm" c="dimmed">
            {finding.clinical_notes}
          </Text>
        )}

        {thumbnails.length > 0 && (
          <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} spacing="sm">
            {thumbnails.map((img) => (
              <ImageThumb
                key={img.id}
                image={img}
                onOpen={() => onZoomImage(img)}
                onRemove={canTriage ? () => onRemoveImage(img) : undefined}
              />
            ))}
          </SimpleGrid>
        )}

        {pending.length > 0 && (
          <Stack gap={4}>
            {pending.map((img) => (
              <Group key={img.id} gap="xs" align="center" wrap="wrap">
                <Badge size="sm" variant="light" color={imageStatusColor(img.ingestion_status)}>
                  {img.ingestion_status}
                </Badge>
                <Text size="sm">{img.capture_type}</Text>
                {img.ingestion_retry_count > 0 && (
                  <Text size="xs" c="dimmed">
                    retried {img.ingestion_retry_count}×
                  </Text>
                )}
                {canTriage && (
                  <Button
                    size="xs"
                    variant="light"
                    onClick={() => onRetryImage(finding.id, img.id)}
                  >
                    Retry
                  </Button>
                )}
              </Group>
            ))}
          </Stack>
        )}

        <FindingAiPanel reviews={reviews} optimisticQueued={optimisticQueued} />
      </Stack>
    </Card>
  );
}
